import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tVulnType, VULN_TYPES, VULN_TYPE_KEYS,
  SEVERITY, SEVERITY_KEYS, VULN_STATUS, VULN_STATUS_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { SevBadge, VulnStatusBadge, EmptyState } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Plus, Search } from "lucide-react";

const EMPTY = {
  title: "", description: "", vulnerability_type: "OTHER", severity: "MEDIUM",
  status: "OPEN", source: "", source_reference: "", cve_id: "",
};

export default function Vulnerabilities() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSev, setFSev] = useState("all");
  const [fType, setFType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const writable = can("vulnerability:write");

  const { data: vulns = [], isLoading } = useQuery({
    queryKey: ["vulnerabilities", q, fStatus, fSev, fType],
    queryFn: async () => {
      const params = {};
      if (q) params.q = q;
      if (fStatus !== "all") params.status = fStatus;
      if (fSev !== "all") params.severity = fSev;
      if (fType !== "all") params.vulnerability_type = fType;
      return (await api.get("/vulnerabilities", { params })).data;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (!payload.cve_id) delete payload.cve_id;
      return (await api.post("/vulnerabilities", payload)).data;
    },
    onSuccess: () => {
      toast.success("Vulnérabilité créée");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <AppShell
      title="Vulnérabilités"
      subtitle={`${vulns.length} vulnérabilité(s) — objet cyber dédié (jamais un NODE)`}
      actions={writable && (
        <Button size="sm" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="create-vulnerability-button">
          <Plus className="h-4 w-4" /> Nouvelle vulnérabilité
        </Button>
      )}
    >
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="vuln-filters">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (titre)…"
            className="pl-9 rounded-sm" data-testid="vuln-search-input" />
        </div>
        <FilterSelect label="Statut" value={fStatus} onChange={setFStatus} testid="filter-status"
          options={VULN_STATUS_KEYS.map((k) => [k, VULN_STATUS[k].label])} />
        <FilterSelect label="Gravité" value={fSev} onChange={setFSev} testid="filter-severity"
          options={SEVERITY_KEYS.map((k) => [k, SEVERITY[k].label])} />
        <FilterSelect label="Type" value={fType} onChange={setFType} testid="filter-type"
          options={VULN_TYPE_KEYS.map((k) => [k, VULN_TYPES[k]])} />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : vulns.length === 0 ? (
        <EmptyState icon={ShieldAlert} title="Aucune vulnérabilité" hint="Créez une vulnérabilité ou ajustez les filtres." />
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="vulnerabilities-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Titre</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Gravité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>CVE</TableHead>
                <TableHead className="text-right">Actifs affectés</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vulns.map((v) => (
                <TableRow key={v.id} className="border-border cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/vulnerabilities/${v.id}`)} data-testid={`vuln-row-${v.id}`}>
                  <TableCell className="font-medium">{v.title}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tVulnType(v.vulnerability_type)}</TableCell>
                  <TableCell><SevBadge value={v.severity} /></TableCell>
                  <TableCell><VulnStatusBadge value={v.status} /></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{v.cve_id || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className="rounded-sm font-mono text-xs" data-testid={`vuln-asset-count-${v.id}`}>
                      {v.asset_count ?? 0}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="vulnerability-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouvelle vulnérabilité</DialogTitle>
            <DialogDescription>La gravité est intrinsèque — ce n'est jamais un niveau de risque CROS.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 rounded-sm" data-testid="vuln-title-input" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.vulnerability_type} onValueChange={(v) => setForm({ ...form, vulnerability_type: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="vuln-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VULN_TYPE_KEYS.map((k) => <SelectItem key={k} value={k}>{VULN_TYPES[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Gravité</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="vuln-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITY_KEYS.map((k) => <SelectItem key={k} value={k}>{SEVERITY[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="vuln-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VULN_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{VULN_STATUS[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">CVE (optionnel)</Label>
              <Input value={form.cve_id} onChange={(e) => setForm({ ...form, cve_id: e.target.value })}
                placeholder="CVE-2026-…" className="mt-1 rounded-sm font-mono" data-testid="vuln-cve-input" />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Référence source</Label>
              <Input value={form.source_reference} onChange={(e) => setForm({ ...form, source_reference: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => create.mutate()} disabled={!form.title || create.isPending} data-testid="save-vulnerability-button">
              {create.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-sm w-[190px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous</SelectItem>
          {options.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
